/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { TextProps } from '#PRIVATE_VisSVGText/text.types';
import { Group, Scale } from '#utils/UNSAFE_visTypes/chart';

type LevelInfo = {
  maxWidth: number;
  maxHeight: number;
  isRotated: boolean;
  numOfSkips: number;
  tickLabels: (TextProps & {
    index: number;
  })[];
};

export type LevelsInfo = Map<number, LevelInfo>;

export const SEPARATOR_GAP = 6;

export type GroupLevelItem = {
  item: Group;
  start: number;
  end: number;
  position: number;
  path: number[];
};

export function getTicks(startIndex: number, endIndex: number) {
  const ticks = [];
  for (let i = startIndex; i <= endIndex; i++) {
    ticks.push(i);
  }
  return ticks;
}

export function getLevelStartingGroup(
  levelsArray: GroupLevelItem[][],
  levelIndex: number,
  viewportMin: number,
  min: number,
  max: number,
  xScale: Scale,
  isRtl: boolean,
  isHoriz: boolean
) {
  const noOfGroups = levelsArray[levelIndex].length;
  const minCoord = xScale.transform(viewportMin);
  for (let i = 0; i < noOfGroups; i++) {
    const labelCoord = getLabelCoord(levelIndex, i, min, max, levelsArray, xScale);
    if (((!isRtl || !isHoriz) && labelCoord >= minCoord) || (isRtl && labelCoord <= minCoord)) {
      return i;
    }
  }
  return noOfGroups;
}

export function getLevelEndingGroup(
  levelsArray: GroupLevelItem[][],
  levelIndex: number,
  viewportMax: number,
  min: number,
  max: number,
  xScale: Scale,
  isRtl: boolean,
  isHoriz: boolean
) {
  const noOfGroups = levelsArray[levelIndex].length;
  const maxCoord = xScale.transform(viewportMax);
  for (let i = noOfGroups - 1; i >= 0; i--) {
    const labelCoord = getLabelCoord(levelIndex, i, min, max, levelsArray, xScale);
    if (((!isRtl || !isHoriz) && labelCoord <= maxCoord) || (isRtl && labelCoord >= maxCoord)) {
      return i;
    }
  }
  return 0;
}

/**
 * Returns the index of starting leaf group.
 * @param levelsArray The levelsArray
 * @param groupIndex The index of the group in that level.
 * @param levelIndex The index of the level.
 * @returns { number } The index of the starting leaf group.
 */
export function getLeafStartIndex(
  levelsArray: GroupLevelItem[][],
  groupIndex: number,
  levelIndex?: number
) {
  if (levelIndex == null) {
    levelIndex = levelsArray.length - 1;
  }
  return levelsArray[levelIndex][groupIndex]['start'];
}

/**
 * Returns the index of last leaf group.
 * @param levelsArray The levelsArray
 * @param groupIndex The index of the group in that level.
 * @param levelIndex The index of the level.
 * @returns { number } The index of the last leaf group.
 */
export function getLeafEndIndex(
  levelsArray: GroupLevelItem[][],
  groupIndex: number,
  levelIndex?: number
) {
  if (levelIndex == null) {
    levelIndex = levelsArray.length - 1;
  }
  return levelsArray[levelIndex][groupIndex]['end'];
}

export function getLabelCoord(
  level: number,
  index: number,
  minValue: number,
  maxValue: number,
  levelsArray: GroupLevelItem[][],
  xScale: Scale
) {
  let startValue = getLeafStartIndex(levelsArray, index, level);
  let endValue = getLeafEndIndex(levelsArray, index, level);
  if (startValue < minValue && endValue > minValue) startValue = minValue;
  if (endValue > maxValue && startValue < maxValue) endValue = maxValue;
  const center = endValue ? startValue + (endValue - startValue) / 2 : startValue;
  return xScale.transform(center);
}

export function getLabelAt(
  index: number,
  level: number | undefined,
  levelsArray: GroupLevelItem[][]
) {
  const numLevels = levelsArray.length;
  if (level == null) level = numLevels - 1;

  index = Math.round(index);
  if (index < 0) return '';

  const labelGroup =
    levelsArray[level] && levelsArray[level][index] ? levelsArray[level][index]['item'] : null;

  let label = '';
  if (labelGroup) {
    if (labelGroup['name']) label = labelGroup['name'];
    else if (labelGroup['id'] != null)
      // Empty or null group name allowed if id is specified
      label = '';
  }
  return label;
}

export const getPosition = (index: number, level: number, levelsArray: GroupLevelItem[][]) => {
  if (level == null) level = levelsArray.length - 1;
  return levelsArray[level][index]['position'];
};

function generateLevelsRecursive(
  groupsArray: Group[],
  level: number,
  levelsArray: GroupLevelItem[][],
  groupIndex: number,
  path: number[]
) {
  for (let i = 0; i < groupsArray.length; i++) {
    // Add new array if at first group in a new level
    if (!levelsArray[level]) levelsArray[level] = [];

    const p = path.slice();
    p.push(i);

    // Store object for group
    levelsArray[level].push({
      item: groupsArray[i],
      start: groupIndex,
      end: groupIndex,
      position: i,
      path: p
    });

    if (!groupsArray[i] || !groupsArray[i]['groups']) {
      groupIndex++;
      continue;
    }

    const lastIndex = levelsArray[level].length - 1;

    // Find the index of the last innermost group nested within this group item
    const currentLeafIndex = generateLevelsRecursive(
      groupsArray[i]['groups']!,
      level + 1,
      levelsArray,
      levelsArray[level][lastIndex]['start'],
      p
    );

    if (groupIndex != currentLeafIndex) {
      levelsArray[level][lastIndex]['end'] = currentLeafIndex - 1; // start and end index used for centering group labels
      groupIndex = currentLeafIndex;
    } else {
      groupIndex++;
    }
  }
  return groupIndex;
}

export function generateLevels(groups: Group[]) {
  const levelsArray: GroupLevelItem[][] = [];
  const groupCount = generateLevelsRecursive(groups, 0, levelsArray, 0, []);

  const getGroupFromPath = (path: number[]) => {
    let root: Group[] | undefined = groups;
    const ids: string[] = [];
    path.forEach((value) => {
      if (!root) {
        return;
      }
      const g = root[value];
      root = g.groups;
      ids.push(g.name || g.id);
    });
    return ids;
  };

  return {
    getGroupFromPath,
    groupCount,
    levelsArray
  };
}
